import React, { useState, useEffect } from 'react';
import { ChevronRight } from 'lucide-react';
import { useLanguage } from '../../context/LanguageContext';
import { useNavigate } from 'react-router-dom';

const HeroSlider = () => {
  const { translations } = useLanguage();
  const navigate = useNavigate();
  const [currentSlide, setCurrentSlide] = useState(0);
  const slides = translations?.heroSlides || [];

  useEffect(() => {
    if (slides.length <= 1) return;
    const timer = setTimeout(() => {
      setCurrentSlide((prev) => (prev + 1) % slides.length);
    }, 12000);
    return () => clearTimeout(timer);
  }, [currentSlide, slides.length]);

  if (!slides.length) return null;

  return (
    <section className="relative h-[600px] md:h-[700px] overflow-hidden">
      {slides.map((slide, index) => (
        <div
          key={slide.id}
          className={`absolute inset-0 transition-opacity duration-1000 ${
            index === currentSlide ? 'opacity-100' : 'opacity-0'
          }`}
        >
          {/* Background Image */}
          <div
            className="absolute inset-0 bg-cover bg-center"
            style={{ backgroundImage: `url(${slide.image})` }}
          >
            <div className="absolute inset-0 bg-black/40" />
          </div>

          {/* Content */}
          <div className="relative z-10 max-w-7xl mx-auto px-4 h-full flex items-center">
            <div className="bg-white p-8 md:p-12 max-w-xl shadow-2xl">
              <h1 className="text-3xl md:text-4xl lg:text-5xl font-bold text-[#1D1D1D] mb-4 leading-tight">
                {slide.title}
              </h1>
              <p className="text-lg text-gray-600 mb-6 leading-relaxed">
                {slide.subtitle}
              </p>
              {slide.link ? (
                <button
                  onClick={() => navigate(slide.link)}
                  data-testid="hero-conference-register-button"
                  className="inline-flex items-center gap-2 bg-[#CD0000] text-white px-6 py-4 font-bold hover:bg-[#c91560] transition-colors animate-heartbeat"
                >
                  {slide.cta} <ChevronRight className="w-5 h-5" />
                </button>
              ) : (
                <a
                  href="#take-action"
                  className="inline-flex items-center gap-2 bg-[#CD0000] text-white px-6 py-4 font-bold hover:bg-[#c91560] transition-colors"
                >
                  {slide.cta} <ChevronRight className="w-5 h-5" />
                </a>
              )}
            </div>
          </div>
        </div>
      ))}

      {/* Slide Indicators */}
      <div className="absolute bottom-8 left-1/2 transform -translate-x-1/2 z-20 flex gap-2">
        {slides.map((slide, index) => (
          <button
            key={slide.id}
            onClick={() => setCurrentSlide(index)}
            className={`flex items-center gap-2 px-4 py-3 transition-all ${
              index === currentSlide
                ? 'bg-[#CD0000] text-white'
                : 'bg-[#1D1D1D]/80 text-white hover:bg-[#1D1D1D]'
            }`}
          >
            <span className="text-sm font-medium">0{index + 1}</span>
            <span className="hidden md:inline text-sm truncate max-w-[150px]">
              {slide.title.split(' ').slice(0, 3).join(' ')}
            </span>
          </button>
        ))}
      </div>
    </section>
  );
};

export default HeroSlider;
